// frontend/src/pages/VendorBulkOrder.jsx - COMPLETE BULK ORDER SYSTEM
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  ArrowRight, 
  Package, 
  Users, 
  MapPin, 
  Calendar, 
  DollarSign, 
  CheckCircle, 
  AlertCircle,
  Search,
  Filter,
  Star,
  Clock,
  MessageSquare,
  Plus,
  Eye,
  Target
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import vendorDataService from '../services/VendorDataService';
import { generateDemoGroups } from '../utils/groupData';

const VendorBulkOrder = () => {
  const navigate = useNavigate();
  const { groupId } = useParams(); // If coming from specific group
  const { user } = useAuth();
  const { addNotification } = useNotification();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [vendorData, setVendorData] = useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [vendorProducts, setVendorProducts] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Bulk order form data
  const [bulkOrderData, setBulkOrderData] = useState({
    targetGroupId: groupId || '',
    targetGroup: null,
    selectedProductId: '',
    selectedProduct: null,
    tierPricing: [
      { minQuantity: 10, maxQuantity: 24, discount: 10, price: 0 },
      { minQuantity: 25, maxQuantity: 49, discount: 15, price: 0 },
      { minQuantity: 50, maxQuantity: 999, discount: 20, price: 0 }
    ],
    deliveryOptions: {
      pickup: true,
      delivery: false,
      deliveryFee: 0,
      deliveryRadius: 10
    },
    timeline: {
      orderDeadline: '',
      fulfillmentDate: '',
      flexible: true
    },
    terms: {
      minimumOrder: 10,
      paymentTerms: 'upfront',
      cancellationPolicy: 'standard'
    },
    message: ''
  });

  const steps = [
    { id: 1, title: 'Group Discovery', description: 'Find groups to target' },
    { id: 2, title: 'Product Selection', description: 'Choose products to offer' },
    { id: 3, title: 'Pricing Tiers', description: 'Set bulk pricing' },
    { id: 4, title: 'Terms & Delivery', description: 'Configure logistics' },
    { id: 5, title: 'Review & Send', description: 'Finalize your offer' }
  ];

  // Load data on mount
  useEffect(() => {
    loadInitialData();
  }, []);

  // Filter groups based on search and category
  useEffect(() => {
    filterGroups();
  }, [availableGroups, searchTerm, categoryFilter]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Get current vendor
      const currentVendor = localStorage.getItem('currentVendor');
      if (currentVendor) {
        const vendor = JSON.parse(currentVendor);
        setVendorData(vendor);
        
        // Load vendor products
        const products = vendorDataService.getVendorProducts(vendor.id);
        setVendorProducts(products);
      }
      
      // Load available groups
      const groups = generateDemoGroups();
      // Filter out completed groups and add targeting info
      const targetableGroups = groups
        .filter(group => !group.isCompleted && group.currentCount > 5) // Only groups with reasonable member count
        .map(group => ({
          ...group,
          targetingScore: calculateTargetingScore(group),
          estimatedSavings: calculateEstimatedSavings(group),
          adminContact: group.creatorName || 'Group Admin'
        }));
      
      setAvailableGroups(targetableGroups);
      
      // If coming from specific group, pre-select it
      if (groupId) {
        const targetGroup = targetableGroups.find(g => g.id === groupId);
        if (targetGroup) {
          setBulkOrderData(prev => ({
            ...prev,
            targetGroupId: groupId,
            targetGroup: targetGroup
          }));
          setCurrentStep(2); // Skip to product selection
        }
      }
      
    } catch (error) {
      console.error('Error loading bulk order data:', error);
      addNotification({
        type: 'error',
        title: 'Loading Error',
        message: 'Failed to load bulk order data',
        showToast: true
      });
    } finally {
      setLoading(false);
    }
  };

  const filterGroups = () => {
    let filtered = [...availableGroups];
    
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(group => 
        group.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(group => group.category === categoryFilter);
    }
    
    // Sort by targeting score (best matches first)
    filtered.sort((a, b) => b.targetingScore - a.targetingScore);
    
    setFilteredGroups(filtered);
  };

  // Calculate how good a match this group is for targeting
  const calculateTargetingScore = (group) => {
    let score = 0;
    
    // Group size (larger groups = higher score)
    score += Math.min(group.currentCount * 2, 50);
    
    // Group activity (closer to deadline = more urgent)
    const now = new Date();
    const deadline = new Date(group.endDate);
    const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 7) score += 30; // Urgent groups
    else if (daysLeft <= 14) score += 20;
    else if (daysLeft <= 30) score += 10;
    
    // Progress (groups close to completing are good targets)
    if (group.progress > 80) score += 25;
    else if (group.progress > 60) score += 15;
    else if (group.progress > 40) score += 10;
    
    // Trust score if available
    if (group.creatorTrust && group.creatorTrust > 3.5) {
      score += 15;
    }
    
    return Math.min(score, 100);
  };

  const calculateEstimatedSavings = (group) => {
    // Estimate potential member savings with bulk pricing
    const avgSavings = group.savings || 25;
    return Math.round(avgSavings * 1.2); // Vendor bulk pricing could increase savings
  };

  const handleGroupSelection = (group) => {
    setBulkOrderData(prev => ({
      ...prev,
      targetGroupId: group.id,
      targetGroup: group
    }));
    setCurrentStep(2);
  };

  const handleProductSelection = (product) => {
    // Calculate tier pricing based on product base price
    const basePrice = product.basePrice;
    const updatedTierPricing = bulkOrderData.tierPricing.map(tier => ({
      ...tier,
      price: Math.round((basePrice * (1 - tier.discount / 100)) * 100) / 100
    }));
    
    setBulkOrderData(prev => ({
      ...prev,
      selectedProductId: product.id,
      selectedProduct: product,
      tierPricing: updatedTierPricing
    }));
    setCurrentStep(3);
  };

  const handleTierPricingChange = (index, field, value) => {
    const updatedTiers = [...bulkOrderData.tierPricing];
    updatedTiers[index] = { ...updatedTiers[index], [field]: value };
    
    // Recalculate price if discount changed
    if (field === 'discount') {
      updatedTiers[index].price = Math.round((bulkOrderData.selectedProduct.basePrice * (1 - value / 100)) * 100) / 100;
    }
    
    setBulkOrderData(prev => ({
      ...prev,
      tierPricing: updatedTiers
    }));
  };

  const handleSubmitBulkOrder = async () => {
    setLoading(true);
    try {
      // Create bulk order proposal
      const proposalData = {
        vendorId: vendorData.id,
        vendorName: vendorData.businessName,
        groupId: bulkOrderData.targetGroupId,
        groupTitle: bulkOrderData.targetGroup.title,
        productId: bulkOrderData.selectedProductId,
        productName: bulkOrderData.selectedProduct.name,
        tierPricing: bulkOrderData.tierPricing,
        deliveryOptions: bulkOrderData.deliveryOptions,
        timeline: bulkOrderData.timeline,
        terms: bulkOrderData.terms,
        message: bulkOrderData.message,
        estimatedSavings: calculateEstimatedSavings(bulkOrderData.targetGroup),
        createdAt: new Date().toISOString(),
        status: 'sent'
      };
      
      // Save proposal using vendor data service
      const proposal = vendorDataService.createBulkOrderProposal(vendorData.id, bulkOrderData.targetGroupId, proposalData);
      
      addNotification({
        type: 'success',
        title: 'Bulk Order Sent!',
        message: `Your offer has been sent to ${bulkOrderData.targetGroup.title}`,
        showToast: true
      });
      
      // Navigate back to dashboard
      navigate('/vendor/dashboard');
      
    } catch (error) {
      console.error('Error creating bulk order:', error);
      addNotification({
        type: 'error',
        title: 'Send Error',
        message: 'Failed to send bulk order proposal',
        showToast: true
      });
    } finally {
      setLoading(false);
    }
  };

  const getStepStatus = (stepNumber) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'current';
    return 'upcoming';
  };

  const canProceedToNext = () => {
    switch (currentStep) {
      case 1: return bulkOrderData.targetGroupId;
      case 2: return bulkOrderData.selectedProductId;
      case 3: return bulkOrderData.tierPricing.every(tier => tier.price > 0);
      case 4: return bulkOrderData.timeline.orderDeadline && bulkOrderData.timeline.fulfillmentDate;
      case 5: return true;
      default: return false;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading bulk order system...</p>
        </div>
      </div>
    );
  }

  const renderStepIndicator = () => (
    <div className="bg-white border-b px-6 py-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                getStepStatus(step.id) === 'completed' 
                  ? 'bg-green-500 text-white' 
                  : getStepStatus(step.id) === 'current'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {getStepStatus(step.id) === 'completed' ? (
                  <CheckCircle size={16} />
                ) : (
                  step.id
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  getStepStatus(step.id) === 'current' ? 'text-blue-600' : 'text-gray-900'
                }`}>
                  {step.title}
                </p>
                <p className="text-xs text-gray-500">{step.description}</p>
              </div>
              {index < steps.length - 1 && (
                <div className={`mx-8 h-px flex-1 ${
                  getStepStatus(step.id) === 'completed' ? 'bg-green-500' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep1GroupDiscovery = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Find Your Target Groups</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Browse active community groups that match your products. Each group represents potential bulk order opportunities.
        </p>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search groups by name, category, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Categories</option>
            <option value="Groceries">Groceries</option>
            <option value="Household">Household</option>
            <option value="Electronics">Electronics</option>
            <option value="Personal Care">Personal Care</option>
            <option value="Baby">Baby</option>
            <option value="Home & Garden">Home & Garden</option>
            <option value="Health & Wellness">Health & Wellness</option>
            <option value="Arts & Crafts">Arts & Crafts</option>
          </select>
        </div>
      </div>

      {/* Groups Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredGroups.map(group => (
          <div key={group.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow border">
            <div className="p-6">
              {/* Group Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-gray-900 mb-1">{group.title}</h3>
                  <p className="text-sm text-gray-600">{group.category}</p>
                </div>
                <div className="flex flex-col items-end">
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    group.targetingScore > 70 ? 'bg-green-100 text-green-700' :
                    group.targetingScore > 50 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {group.targetingScore > 70 ? 'Hot Lead' :
                     group.targetingScore > 50 ? 'Good Match' : 'Potential'}
                  </div>
                  <div className="flex items-center mt-1">
                    <Star size={12} className="text-yellow-500 fill-current" />
                    <span className="text-xs text-gray-600 ml-1">{group.targetingScore}/100</span>
                  </div>
                </div>
              </div>

              {/* Group Stats */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Members:</span>
                  <span className="font-medium">{group.currentCount}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Progress:</span>
                  <span className="font-medium">{group.progress}%</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Potential Savings:</span>
                  <span className="font-medium text-green-600">+{group.estimatedSavings}%</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Deadline:</span>
                  <span className="font-medium">{Math.ceil((new Date(group.endDate) - new Date()) / (1000 * 60 * 60 * 24))} days</span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>{group.currentUnits} units</span>
                  <span>{group.nextBreakCount} needed</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${group.progress}%` }}
                  />
                </div>
              </div>

              {/* Location */}
              <div className="flex items-center text-sm text-gray-600 mb-4">
                <MapPin size={14} className="mr-1" />
                <span>{group.location}</span>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleGroupSelection(group)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm"
                >
                  Target This Group
                </button>
                <button
                  className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  title="View group details"
                >
                  <Eye size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredGroups.length === 0 && (
        <div className="text-center py-12">
          <Target size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Groups Found</h3>
          <p className="text-gray-600">Try adjusting your search terms or filters to find targetable groups.</p>
        </div>
      )}
    </div>
  );

  const renderStep2ProductSelection = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Product to Offer</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Choose which product you want to offer to <strong>{bulkOrderData.targetGroup?.title}</strong>. 
          Consider products that match their category and needs.
        </p>
      </div>

      {/* Selected Group Info */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h3 className="font-medium text-blue-900 mb-2">Target Group: {bulkOrderData.targetGroup?.title}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-blue-600">Members:</span>
            <p className="font-medium">{bulkOrderData.targetGroup?.currentCount}</p>
          </div>
          <div>
            <span className="text-blue-600">Category:</span>
            <p className="font-medium">{bulkOrderData.targetGroup?.category}</p>
          </div>
          <div>
            <span className="text-blue-600">Current Price:</span>
            <p className="font-medium">${bulkOrderData.targetGroup?.currentPrice}</p>
          </div>
          <div>
            <span className="text-blue-600">Savings Goal:</span>
            <p className="font-medium">{bulkOrderData.targetGroup?.savings}%</p>
          </div>
        </div>
      </div>

      {/* Product Selection */}
      {vendorProducts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vendorProducts.map(product => {
            const isSelected = product.id === bulkOrderData.selectedProductId;
            const matchesCategory = product.category === bulkOrderData.targetGroup?.category;
            
            return (
              <div 
                key={product.id} 
                className={`bg-white rounded-lg shadow border-2 transition-all cursor-pointer ${
                  isSelected 
                    ? 'border-blue-500 ring-2 ring-blue-200' 
                    : matchesCategory
                    ? 'border-green-200 hover:border-green-400'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handleProductSelection(product)}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{product.name}</h3>
                      <p className="text-sm text-gray-600">{product.category}</p>
                    </div>
                    {matchesCategory && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                        Perfect Match
                      </span>
                    )}
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Base Price:</span>
                      <span className="font-medium">${product.basePrice}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total Sold:</span>
                      <span className="font-medium">{product.totalSold || 0} units</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Inventory:</span>
                      <span className={`font-medium ${
                        product.inventory === 'In Stock' ? 'text-green-600' : 'text-yellow-600'
                      }`}>
                        {product.inventory}
                      </span>
                    </div>
                  </div>

                  {product.description && (
                    <p className="text-sm text-gray-600 mb-4">{product.description}</p>
                  )}

                  <button
                    onClick={() => handleProductSelection(product)}
                    className={`w-full px-4 py-2 rounded-lg font-medium text-sm ${
                      isSelected
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {isSelected ? 'Selected' : 'Select Product'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <Package size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Products Available</h3>
          <p className="text-gray-600 mb-6">You need to add products before creating bulk orders.</p>
          <button
            onClick={() => navigate('/vendor/dashboard?tab=products')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Add Products First
          </button>
        </div>
      )}
    </div>
  );

  const renderStep3PricingTiers = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Set Bulk Pricing Tiers</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Create attractive bulk pricing for <strong>{bulkOrderData.selectedProduct?.name}</strong>. 
          Higher quantities should offer better discounts to incentivize larger orders.
        </p>
      </div>

      {/* Product Info */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-2">Selected Product: {bulkOrderData.selectedProduct?.name}</h3>
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Base Price:</span>
          <span className="font-bold text-lg">${bulkOrderData.selectedProduct?.basePrice}</span>
        </div>
      </div>

      {/* Pricing Tiers */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold mb-4">Bulk Pricing Tiers</h3>
        <div className="space-y-4">
          {bulkOrderData.tierPricing.map((tier, index) => (
            <div key={index} className="border rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min Quantity
                  </label>
                  <input
                    type="number"
                    value={tier.minQuantity}
                    onChange={(e) => handleTierPricingChange(index, 'minQuantity', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Quantity
                  </label>
                  <input
                    type="number"
                    value={tier.maxQuantity}
                    onChange={(e) => handleTierPricingChange(index, 'maxQuantity', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount %
                  </label>
                  <input
                    type="number"
                    value={tier.discount}
                    onChange={(e) => handleTierPricingChange(index, 'discount', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    min="0"
                    max="50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price per Unit
                  </label>
                  <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg">
                    ${tier.price}
                  </div>
                </div>
              </div>
              <div className="mt-3 text-sm text-gray-600">
                <strong>Tier {index + 1}:</strong> {tier.minQuantity}-{tier.maxQuantity} units at ${tier.price} each 
                ({tier.discount}% off base price)
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing Preview */}
      <div className="bg-green-50 rounded-lg p-6 border border-green-200">
        <h3 className="font-semibold text-green-900 mb-4">Member Savings Preview</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {bulkOrderData.tierPricing.map((tier, index) => (
            <div key={index} className="bg-white rounded-lg p-4">
              <h4 className="font-medium mb-2">Tier {index + 1}</h4>
              <p className="text-2xl font-bold text-green-600">{tier.discount}% OFF</p>
              <p className="text-sm text-gray-600">{tier.minQuantity}-{tier.maxQuantity} units</p>
              <p className="text-sm font-medium">${tier.price} per unit</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep4TermsDelivery = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Terms & Delivery Options</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Configure delivery options, timeline, and terms for your bulk order offer.
        </p>
      </div>

      {/* Delivery Options */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold mb-4">Delivery Options</h3>
        <div className="space-y-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={bulkOrderData.deliveryOptions.pickup}
              onChange={(e) => setBulkOrderData(prev => ({
                ...prev,
                deliveryOptions: { ...prev.deliveryOptions, pickup: e.target.checked }
              }))}
              className="rounded text-blue-600"
            />
            <span className="ml-3">
              <strong>Pickup Available</strong>
              <p className="text-sm text-gray-600">Members can pick up orders at your location</p>
            </span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={bulkOrderData.deliveryOptions.delivery}
              onChange={(e) => setBulkOrderData(prev => ({
                ...prev,
                deliveryOptions: { ...prev.deliveryOptions, delivery: e.target.checked }
              }))}
              className="rounded text-blue-600"
            />
            <span className="ml-3">
              <strong>Delivery Available</strong>
              <p className="text-sm text-gray-600">You can deliver orders to members</p>
            </span>
          </label>

          {bulkOrderData.deliveryOptions.delivery && (
            <div className="ml-6 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delivery Fee
                </label>
                <input
                  type="number"
                  value={bulkOrderData.deliveryOptions.deliveryFee}
                  onChange={(e) => setBulkOrderData(prev => ({
                    ...prev,
                    deliveryOptions: { ...prev.deliveryOptions, deliveryFee: parseFloat(e.target.value) }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delivery Radius (miles)
                </label>
                <input
                  type="number"
                  value={bulkOrderData.deliveryOptions.deliveryRadius}
                  onChange={(e) => setBulkOrderData(prev => ({
                    ...prev,
                    deliveryOptions: { ...prev.deliveryOptions, deliveryRadius: parseInt(e.target.value) }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold mb-4">Timeline</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Order Deadline
            </label>
            <input
              type="date"
              value={bulkOrderData.timeline.orderDeadline}
              onChange={(e) => setBulkOrderData(prev => ({
                ...prev,
                timeline: { ...prev.timeline, orderDeadline: e.target.value }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fulfillment Date
            </label>
            <input
              type="date"
              value={bulkOrderData.timeline.fulfillmentDate}
              onChange={(e) => setBulkOrderData(prev => ({
                ...prev,
                timeline: { ...prev.timeline, fulfillmentDate: e.target.value }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              min={bulkOrderData.timeline.orderDeadline || new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>
        <label className="flex items-center mt-4">
          <input
            type="checkbox"
            checked={bulkOrderData.timeline.flexible}
            onChange={(e) => setBulkOrderData(prev => ({
              ...prev,
              timeline: { ...prev.timeline, flexible: e.target.checked }
            }))}
            className="rounded text-blue-600"
          />
          <span className="ml-3 text-sm">Dates are flexible and negotiable</span>
        </label>
      </div>

      {/* Terms */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold mb-4">Terms & Conditions</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum Order Quantity
            </label>
            <input
              type="number"
              value={bulkOrderData.terms.minimumOrder}
              onChange={(e) => setBulkOrderData(prev => ({
                ...prev,
                terms: { ...prev.terms, minimumOrder: parseInt(e.target.value) }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              min="1"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Terms
            </label>
            <select
              value={bulkOrderData.terms.paymentTerms}
              onChange={(e) => setBulkOrderData(prev => ({
                ...prev,
                terms: { ...prev.terms, paymentTerms: e.target.value }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="upfront">Payment upfront</option>
              <option value="on_delivery">Payment on delivery</option>
              <option value="net_15">Net 15 days</option>
              <option value="net_30">Net 30 days</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cancellation Policy
            </label>
            <select
              value={bulkOrderData.terms.cancellationPolicy}
              onChange={(e) => setBulkOrderData(prev => ({
                ...prev,
                terms: { ...prev.terms, cancellationPolicy: e.target.value }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="standard">Standard cancellation (48h notice)</option>
              <option value="flexible">Flexible cancellation (24h notice)</option>
              <option value="strict">No cancellation after confirmation</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep5ReviewSend = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Review & Send Your Offer</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Review all details of your bulk order proposal before sending it to the group admin.
        </p>
      </div>

      {/* Offer Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold mb-6">Bulk Order Proposal Summary</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Target Group */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Target Group</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Group:</span>
                <span className="font-medium">{bulkOrderData.targetGroup?.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Members:</span>
                <span className="font-medium">{bulkOrderData.targetGroup?.currentCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Category:</span>
                <span className="font-medium">{bulkOrderData.targetGroup?.category}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Admin:</span>
                <span className="font-medium">{bulkOrderData.targetGroup?.adminContact}</span>
              </div>
            </div>
          </div>

          {/* Product */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Product Offering</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Product:</span>
                <span className="font-medium">{bulkOrderData.selectedProduct?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Base Price:</span>
                <span className="font-medium">${bulkOrderData.selectedProduct?.basePrice}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Category:</span>
                <span className="font-medium">{bulkOrderData.selectedProduct?.category}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Inventory:</span>
                <span className="font-medium">{bulkOrderData.selectedProduct?.inventory}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Tiers */}
        <div className="mt-6">
          <h4 className="font-medium text-gray-900 mb-3">Bulk Pricing Tiers</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {bulkOrderData.tierPricing.map((tier, index) => (
              <div key={index} className="border rounded-lg p-4 text-center">
                <div className="text-lg font-bold text-green-600">{tier.discount}% OFF</div>
                <div className="text-sm text-gray-600">{tier.minQuantity}-{tier.maxQuantity} units</div>
                <div className="text-lg font-semibold">${tier.price}/unit</div>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline & Terms */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Timeline</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Order Deadline:</span>
                <span className="font-medium">{new Date(bulkOrderData.timeline.orderDeadline).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Fulfillment:</span>
                <span className="font-medium">{new Date(bulkOrderData.timeline.fulfillmentDate).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Flexible:</span>
                <span className="font-medium">{bulkOrderData.timeline.flexible ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-3">Delivery & Terms</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Pickup:</span>
                <span className="font-medium">{bulkOrderData.deliveryOptions.pickup ? 'Available' : 'Not available'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Delivery:</span>
                <span className="font-medium">
                  {bulkOrderData.deliveryOptions.delivery 
                    ? `$${bulkOrderData.deliveryOptions.deliveryFee} (${bulkOrderData.deliveryOptions.deliveryRadius}mi)`
                    : 'Not available'
                  }
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Min Order:</span>
                <span className="font-medium">{bulkOrderData.terms.minimumOrder} units</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Payment:</span>
                <span className="font-medium">{bulkOrderData.terms.paymentTerms.replace('_', ' ')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Personal Message */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold mb-4">Personal Message to Group Admin</h3>
        <textarea
          value={bulkOrderData.message}
          onChange={(e) => setBulkOrderData(prev => ({ ...prev, message: e.target.value }))}
          placeholder="Add a personal message to introduce yourself and explain why this offer would benefit their group..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          rows={4}
        />
      </div>

      {/* Send Actions */}
      <div className="bg-green-50 rounded-lg p-6 border border-green-200">
        <h3 className="font-semibold text-green-900 mb-2">Ready to Send?</h3>
        <p className="text-green-800 text-sm mb-4">
          Your bulk order proposal will be sent to the group admin. They can accept, negotiate, or decline your offer.
        </p>
        <div className="flex items-center gap-4">
          <button
            onClick={handleSubmitBulkOrder}
            disabled={loading}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Sending...
              </>
            ) : (
              <>
                <MessageSquare size={16} className="mr-2" />
                Send Bulk Order Proposal
              </>
            )}
          </button>
          <span className="text-sm text-gray-600">
            This proposal will be tracked in your dashboard
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/vendor/dashboard')}
                className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
              >
                <ArrowLeft size={20} className="mr-2" />
                Back to Dashboard
              </button>
              <div>
                <h1 className="text-2xl font-bold">Create Bulk Order</h1>
                <p className="text-sm text-gray-600">Connect with community groups and create bulk offers</p>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              Step {currentStep} of {steps.length}
            </div>
          </div>
        </div>
      </div>

      {/* Step Indicator */}
      {renderStepIndicator()}

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {currentStep === 1 && renderStep1GroupDiscovery()}
        {currentStep === 2 && renderStep2ProductSelection()}
        {currentStep === 3 && renderStep3PricingTiers()}
        {currentStep === 4 && renderStep4TermsDelivery()}
        {currentStep === 5 && renderStep5ReviewSend()}
      </div>

      {/* Navigation */}
      {currentStep < 5 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex justify-between">
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                disabled={currentStep === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <ArrowLeft size={16} className="mr-2" />
                Previous
              </button>
              
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={!canProceedToNext()}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                Next Step
                <ArrowRight size={16} className="ml-2" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorBulkOrder;